import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import AdminRouter from "./features/admin/AdminRouter";
import FreeFunctionsPage from "./features/free-tools/FreeFunctionsPage";
import ReviewsPromoPage from "./features/promo/ReviewsPromoPage";
import TermsOfServicePage from "./features/legal/TermsOfServicePage";
import PrivacyPage from "./features/legal/PrivacyPage";
import LegalPage from "./pages/LegalPage";
import BlogListing from "./features/blog/BlogListing";
import BlogArticle from "./features/blog/BlogArticle";
import BlogCategory from "./features/blog/BlogCategory";

import "./App.css";
import "./features/chat/ChatSection.css";
import PublicChatPage from "./features/chat/PublicChatPage";

import ChatSection from "./features/chat/ChatSection";
import { useAiChat } from "./hooks/useAiChat";
import PublicProfile from "./components/PublicProfile";
import CookieConsentBanner from "./components/CookieConsentBanner";
import NotificationBar from "./components/NotificationBar";
import {
  SUPPORT_OPEN_TICKET_EVENT,
  SUPPORT_OPEN_TICKET_KEY,
} from "./features/settings/SupportPanel";

import SimpleLanding from "./features/layout/SimpleLanding";
import VerifyEmailPage from "./features/auth/VerifyEmailPage";
import ResetPasswordPage from "./features/auth/ResetPasswordPage";
import VerifyEmailRequestPage from "./features/auth/VerifyEmailRequestPage";
import PasswordResetRequestPage from "./features/auth/PasswordResetRequestPage";
import EmailVerificationBanner from "./features/auth/EmailVerificationBanner";
import YandexAuthCallbackPage from "./features/auth/YandexAuthCallbackPage";
import VkAuthCallbackPage from "./features/auth/VkAuthCallbackPage";

import SettingsSection from "./features/settings/SettingsSection";

import EditAccountModal from "./features/settings/EditAccountModal";

import AccountSection from "./features/account/AccountSection";

import WorkspaceHeader from "./features/layout/WorkspaceHeader";

import WorkspaceSidebar from "./features/layout/WorkspaceSidebar";

import AppStatus from "./features/layout/AppStatus";

import AppFooter from "./features/layout/AppFooter";

import ProjectsSection from "./features/projects/ProjectsSection";

import ProjectModal from "./features/projects/ProjectModal";
import TasksSection from "./features/tasks/TasksSection";
import { useTasksController } from "./features/tasks/useTasksController";

import TaskCreateModal from "./features/tasks/TaskCreateModal";

import TaskDetailModal from "./features/tasks/TaskDetailModal";

import TaskEditModal from "./features/tasks/TaskEditModal";


import MarketplaceSection from "./features/marketplace/MarketplaceSection";
import { useOzonState } from "./features/marketplace/ozon/useOzonState";
import { useWbState } from "./features/marketplace/wb/useWbState";

import CrmSection from "./features/crm/CrmSection";

import HelpSection from "./features/help/HelpSection";

import Lightbox from "./components/Lightbox";

import { useAppState } from "./features/app/useAppState";

import { normalizeId, formatDateTime } from "./utils/formatters";

import { getInitials } from "./utils/users";

import { getAccessRoleLabel } from "./utils/access";

import { copyTextWithStatus } from "./utils/clipboard";
import { openCookieSettings } from "./utils/cookieConsent";
import { trackPageView } from "./utils/analytics";

import { useProjectsController } from "./features/projects/useProjectsController";
import OnboardingTour from "./features/onboarding/OnboardingTour";
import NotFoundPage from "./features/layout/NotFoundPage";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const app = useAppState();
  const {
    token,
    user,
    setUser,
    request,
    authHeader,
    status,
    setStatus,
    showStatus,
    API_BASE_URL,
    lightbox,
    openLightbox,
    closeLightbox,
    goLightbox,
    activeSection,
    setActiveSection,
    activeMarketplace,
    setActiveMarketplace,
    activeProjectId,
    setActiveProjectId,
    openProjectId,
    openProjectCard,
    closeProjectCard,
    openMarketplaceTools,
    marketplaces,
    workspaces,
    companyUsers,
    companyInfo,
    setCompanyInfo,
    crmDeals,
    profileSummary,
    profileSummaryBusy,
    profileStats,
    profileStatsBusy,
    profileBusy,
    avatarBusy,
    editAccount,
    editForm,
    setEditForm,
    editBusy,
    projectModalOpen,
    projectEditId,
    projectForm,
    savedProjectFilter,
    setSavedProjectFilter,
    setProjectModalOpen,
    setProjectEditId,
    setProjectForm,
    settingsTab,
    setSettingsTab,
    busy,
    setBusy,
    logoError,
    setLogoError,
    billingSummary,
    orderDynamics,
    orderDynamicsError,
    userPlanFeatures,
    companyId,
    companyMode,
    crmEnabled,
    isAdmin,
    publicSlug,
    logoCyrSrc,
    getWorkspaceById,
    transferAllowedRoles,
    getCabinetRoleLocal,
    getWorkspaceRoleLocal,
    getOwnershipScopeLabelLocal,
    visibleWorkspaces,
    assignedCabinetMap,
    userCabinets,
    availableProjectCabinets,
    loadMarketplacesLocal,
    loadWorkspacesLocal,
    handleLogout,
    handleProfileSave,
    loadProfileStats,
    handleAvatarUpload,
    handleCertificatesUpload,
    handleTelegramLink,
    openEditAccount,
    closeEditAccount,
    handleEditSubmit,
    getUserLabel,
    loadCurrentUserLocal,
    loadCrmDealsLocal,
  } = app;

  const prevPathRef = React.useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev.startsWith('/admin') && !location.pathname.startsWith('/admin') && token) {
      loadCurrentUserLocal?.().catch(() => {});
    }
  }, [location.pathname, token]);

  // Список сделок нужен фильтру и форме задач; грузим при входе в «Задачи», чтобы были и свежие сделки.
  useEffect(() => {
    if (activeSection === "tasks" && token) loadCrmDealsLocal?.().catch(() => {});
  }, [activeSection, token]);

  useEffect(() => {
    if (location.pathname !== "/billing/success") return;
    try {
      window.sessionStorage.setItem("anylex_open_billing", "1");
    } catch (error) {
      // Ignore storage failures and still continue redirect flow.
    }
    navigate("/", { replace: true });
  }, [location.pathname, navigate]);

  useEffect(() => {
    trackPageView(location);
  }, [location.pathname, location.search]);

  const openSupportTicket = React.useCallback(
    (ticketId) => {
      try {
        window.sessionStorage.setItem(SUPPORT_OPEN_TICKET_KEY, String(ticketId));
      } catch (error) {
        // Ignore storage failures — the event below still reaches a mounted panel.
      }
      setActiveSection("settings");
      setSettingsTab("support");
      window.dispatchEvent(
        new CustomEvent(SUPPORT_OPEN_TICKET_EVENT, { detail: { ticketId: String(ticketId) } })
      );
    },
    [setActiveSection, setSettingsTab]
  );

  // Ссылка из письма/уведомления: /?support_ticket=<id> открывает переписку.
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const ticketId = params.get("support_ticket");
    if (!ticketId) return;
    openSupportTicket(ticketId);
    params.delete("support_ticket");
    const search = params.toString();
    navigate({ pathname: "/", search: search ? `?${search}` : "" }, { replace: true });
  }, [token, location.search, navigate, openSupportTicket]);

  const handleNotificationOpen = React.useCallback(
    (link) => {
      try {
        const url = new URL(link, window.location.origin);
        const ticketId = url.searchParams.get("support_ticket");
        if (ticketId) {
          openSupportTicket(ticketId);
          return;
        }
        if (url.searchParams.get("settings") === "cabinets") {
          setActiveSection("settings");
          setSettingsTab("cabinets");
          return;
        }
        if (url.origin === window.location.origin) {
          navigate(`${url.pathname}${url.search}`);
        }
      } catch (error) {
        // Broken link in a notification must not break the banner.
      }
    },
    [navigate, openSupportTicket, setActiveSection, setSettingsTab]
  );

  useEffect(() => {
    if (!token) return;
    let shouldOpenBilling = false;
    try {
      shouldOpenBilling = window.sessionStorage.getItem("anylex_open_billing") === "1";
    } catch (error) {
      shouldOpenBilling = false;
    }
    if (!shouldOpenBilling) return;
    setActiveSection("settings");
    setSettingsTab("billing");
    showStatus("success", "Оплата прошла. Открыт раздел биллинга.");
    try {
      window.sessionStorage.removeItem("anylex_open_billing");
    } catch (error) {
      // Ignore storage failures.
    }
  }, [token, setActiveSection, setSettingsTab, showStatus]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const notifyTosUpdate = async () => {
      try {
        const response = await request("/api/v1/legal/tos/acceptance-status");
        if (cancelled || !response?.requires_reacceptance || !response?.current_version) {
          return;
        }

        const storageKey = `anylex_tos_notice_${response.current_version}`;
        const alreadyShown = window.sessionStorage.getItem(storageKey) === "1";
        if (alreadyShown) {
          return;
        }

        showStatus(
          "info",
          "Опубликована новая редакция условий использования. Откройте /offer и примите актуальную версию."
        );
        window.sessionStorage.setItem(storageKey, "1");

        if (location.pathname !== "/offer") {
          const redirectKey = `anylex_tos_redirect_${response.current_version}`;
          const alreadyRedirected = window.sessionStorage.getItem(redirectKey) === "1";
          if (!alreadyRedirected) {
            window.sessionStorage.setItem(redirectKey, "1");
            navigate("/offer");
          }
        }
      } catch (error) {
        // Ignore ToS notice fetch failures.
      }
    };

    notifyTosUpdate();
    return () => {
      cancelled = true;
    };
  }, [token, request, showStatus, navigate, location.pathname]);

  const handleCopy = (value, label) =>
    copyTextWithStatus(value, label, showStatus);
  const moduleFlags = user?.module_flags || {};
  const showOnboarding =
    !!token && !!user && !user.onboarding_completed_at && !onboardingDismissed;
  const isBalanceRestricted = (billingSummary?.access_level || "") === "blocked";
  const isModuleVisible = (key) =>
    !["hidden", "disabled"].includes(moduleFlags[key] || "enabled");
  const showDisabledSectionMessage =
    !!token &&
    !!activeSection &&
    [
      "marketplace",
      "projects",
      "tasks",
      "chat",
      "crm",
      "settings",
      "account",
    ].includes(activeSection) &&
    !isModuleVisible(activeSection);

  useEffect(() => {
    if (!token || !isBalanceRestricted) return;
    if (activeSection !== "settings" && activeSection !== "help") {
      setActiveSection("settings");
      setSettingsTab("billing");
    }
  }, [token, isBalanceRestricted, activeSection, setActiveSection, setSettingsTab]);

  const tasksController = useTasksController({
    request,
    showStatus,
    setStatus,
    user,
    companyId,
    activeSection,
    activeProjectId,
    workspaces,
    setActiveSection,
    formatDateTime,
    getUserLabel,
  });
  const {
    tasks,
    tasksBusy,
    tasksView,
    setTasksView,
    kanbanGroup,
    setKanbanGroup,
    taskFilters,
    setTaskFilters,
    taskTags,
    sortedTasks,
    kanbanColumns,
    loadTasks,
    setPresetFilters,
    taskStatusLabels,
    taskForm,
    setTaskForm,
    taskModalOpen,
    setTaskModalOpen,
    taskTagsInput,
    setTaskTagsInput,
    taskEditOpen,
    setTaskEditOpen,
    taskEditForm,
    setTaskEditForm,
    taskEditTagsInput,
    setTaskEditTagsInput,
    taskEditBusy,
    setTaskEditBusy,
    selectedTask,
    taskDetail,
    taskDetailBusy,
    taskDetailPanelRef,
    taskDetailSize,
    taskDescriptionExpanded,
    setTaskDescriptionExpanded,
    taskHistory,
    taskHistoryExpanded,
    setTaskHistoryExpanded,
    taskComments,
    taskCommentBody,
    taskCommentFiles,
    setTaskCommentBody,
    setTaskCommentFiles,
    taskEditPanelRef,
    taskEditSize,
    taskCreatePanelRef,
    taskCreateSize,
    startTaskCreateResize,
    handleCreateTask,
    handleCreateTaskForDeal,
    handleOpenTasksForDeal,
    openTaskDetail,
    closeTaskDetail,
    startTaskResize,
    startTaskEditResize,
    openTaskEdit,
    closeTaskEdit,
    toggleEditList,
    handleAddTaskComment,
    handleDeleteTask,
    handleStatusChange,
    handleExecuteAiTask,
    handleExecuteAiTasks,
    handleUpdateTaskFields,
    handleUpdateTask,
    handleTagsBlur,
    handleTagsKeyDown,
    handleEditTagsBlur,
    handleEditTagsKeyDown,
    toggleTaskList,
    canEditTaskDates,
    canChangeStatus,
    handleKanbanDragStart,
    handleKanbanDrop,
    formatHistoryEntry,
  } = tasksController;

  const aiChat = useAiChat({ request, showStatus, workspaceId: activeProjectId });

  const { ozonSectionProps } = useOzonState({
    request,
    showStatus,
    setStatus,
    token,
    activeSection,
    activeMarketplace,
    activeProjectId,
    setActiveProjectId,
    marketplaces,
    workspaces,
    openLightbox,
  });

  const { wbSectionProps } = useWbState({ activeMarketplace, marketplaces });

  const {
    openProjectModal,
    closeProjectModal,
    toggleProjectCabinet,
    saveProject,
    handleDeleteProject,
    openProjectDetail,
    closeProjectDetail,
  } = useProjectsController({
    projectForm,
    projectEditId,
    request,
    setBusy,
    setStatus,
    showStatus,
    loadWorkspaces: loadWorkspacesLocal,
    savedProjectFilter,
    setSavedProjectFilter,
    openProjectCard,
    closeProjectCard,
    setTaskFilters,
    taskFilters,
    setProjectEditId,
    setProjectForm,
    setProjectModalOpen,
  });

  if (publicSlug) {
    return (
      <div className="app">
        <div className="app__glow" />

        <div className="app__shell">
          <PublicProfile slug={publicSlug} openLightbox={openLightbox} />
          <Lightbox
            lightbox={lightbox}
            onClose={closeLightbox}
            onNavigate={goLightbox}
          />
          <CookieConsentBanner />
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/admin/*" element={<AdminRouter />} />
        <Route path="/free-functions/*" element={<FreeFunctionsPage />} />
        <Route path="/promo/reviews" element={<ReviewsPromoPage />} />
        <Route path="/offer" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/legal" element={<LegalPage fileName="index.md" />} />
        <Route path="/privacy-consent" element={<LegalPage fileName="privacy-consent.md" />} />
        <Route path="/ads-consent" element={<LegalPage fileName="ads-consent.md" />} />
        <Route path="/it-company-info" element={<LegalPage fileName="it-company-info.md" />} />
        <Route path="/blog" element={<BlogListing />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/blog/category/:categorySlug" element={<BlogCategory />} />
        <Route path="/share/chat/:token" element={<PublicChatPage />} />
        <Route
          path="/oauth/yandex/callback"
          element={<YandexAuthCallbackPage />}
        />
        <Route path="/oauth/vk/callback" element={<VkAuthCallbackPage />} />
        <Route path="/oauth/vk/callback/" element={<VkAuthCallbackPage />} />
        <Route
          path="/verify-email/request"
          element={<VerifyEmailRequestPage />}
        />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/password-reset/request"
          element={<PasswordResetRequestPage />}
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="*"
          element={
            location.pathname !== '/' && !location.pathname.startsWith('/app') ? <NotFoundPage /> :
            <div className="app">
              <main className="app__shell">
                {!token && (
                  <SimpleLanding
                    logoCyrSrc={logoCyrSrc}
                    logoError={logoError}
                    setLogoError={setLogoError}
                  />
                )}

              {status && <AppStatus status={status} />}

              {!token ? null : (
                <section className="workspace">
                  {showOnboarding && (
                    <OnboardingTour
                      request={request}
                      setUser={setUser}
                      showStatus={showStatus}
                      onClose={() => setOnboardingDismissed(true)}
                      onNavigate={(target) => {
                        if (target === "projects") {
                          setActiveSection("projects");
                          return;
                        }
                        setActiveSection("settings");
                        setSettingsTab(target === "billing" ? "billing" : "cabinets");
                      }}
                    />
                  )}

                  <NotificationBar token={token} onOpen={handleNotificationOpen} />

                  {user && !user.email_verified && (
                    <EmailVerificationBanner
                      onOpenAccount={() => {
                        setActiveSection("settings");
                        setSettingsTab("account");
                      }}
                    />
                  )}

                  <WorkspaceHeader
                    token={token}
                    onNotificationOpen={handleNotificationOpen}
                    logoCyrSrc={logoCyrSrc}
                    logoError={logoError}
                    setLogoError={setLogoError}
                    setActiveSection={setActiveSection}
                    setSettingsTab={setSettingsTab}
                    billingSummary={billingSummary}
                    orderDynamics={orderDynamics}
                    orderDynamicsError={orderDynamicsError}
                    handleLogout={handleLogout}
                  />

                  <div className="workspace__layout">
                    <WorkspaceSidebar
                      activeSection={activeSection}
                      setActiveSection={setActiveSection}
                      activeMarketplace={activeMarketplace}
                      setActiveMarketplace={setActiveMarketplace}
                      crmEnabled={crmEnabled}
                      moduleFlags={moduleFlags}
                      userPlanFeatures={userPlanFeatures}
                      isBalanceRestricted={isBalanceRestricted}
                      onOpenBilling={() => {
                        setActiveSection("settings");
                        setSettingsTab("billing");
                      }}
                    />

                    <div className="workspace__content">
                      {activeSection === "settings" &&
                        isModuleVisible("settings") && (
                          <SettingsSection
                            companyId={companyId}
                            companyInfo={companyInfo}
                            setCompanyInfo={setCompanyInfo}
                            marketplaces={marketplaces}
                            workspaces={workspaces}
                            companyUsers={companyUsers}
                            setActiveSection={setActiveSection}
                            loadMarketplaces={loadMarketplacesLocal}
                            loadWorkspaces={loadWorkspacesLocal}
                            settingsTab={settingsTab}
                            setSettingsTab={setSettingsTab}
                            openProjectModal={openProjectModal}
                          />
                        )}

                      {activeSection === "account" &&
                        isModuleVisible("account") && (
                          <AccountSection
                            user={user}
                            summary={profileSummary}
                            summaryBusy={profileSummaryBusy}
                            stats={profileStats}
                            onSave={handleProfileSave}
                            onAvatarUpload={handleAvatarUpload}
                            onCopy={handleCopy}
                            openLightbox={openLightbox}
                            busy={profileBusy}
                            avatarBusy={avatarBusy}
                            showStatus={showStatus}
                            onProfileUpdate={loadCurrentUserLocal}
                          />
                        )}

                      {activeSection === "projects" &&
                        isModuleVisible("projects") && (
                          <ProjectsSection
                            activeProjectId={openProjectId}
                            visibleWorkspaces={visibleWorkspaces}
                            user={user}
                            marketplaces={marketplaces}
                            companyUsers={companyUsers}
                            tasksView={tasksView}
                            tasksBusy={tasksBusy}
                            tasks={sortedTasks}
                            onViewChange={setTasksView}
                            onPreset={setPresetFilters}
                            taskFilters={taskFilters}
                            setTaskFilters={setTaskFilters}
                            taskStatusLabels={taskStatusLabels}
                            taskTags={taskTags}
                            getWorkspaceById={getWorkspaceById}
                            getWorkspaceRole={getWorkspaceRoleLocal}
                            getUserLabel={getUserLabel}
                            getAccessRoleLabel={getAccessRoleLabel}
                            openProjectDetail={openProjectDetail}
                            closeProjectDetail={closeProjectDetail}
                            openProjectModal={openProjectModal}
                            openMarketplaceProjectTools={(
                              marketplaceType,
                              workspaceId,
                            ) => {
                              // Явно переносим выбранный проект в раздел
                              // маркетплейса: полагаться на sync-эффекты нельзя —
                              // они молча откатывались на первый проект.
                              openMarketplaceTools(marketplaceType, workspaceId);
                            }}
                            loadTasks={loadTasks}
                            onOpenTask={openTaskDetail}
                            kanbanColumns={kanbanColumns}
                            kanbanGroup={kanbanGroup}
                            getInitials={getInitials}
                            onDragStart={handleKanbanDragStart}
                            onDrop={handleKanbanDrop}
                          />
                        )}

                      {activeSection === "tasks" &&
                        isModuleVisible("tasks") && (
                          <TasksSection
                            onCreateTask={() => setTaskModalOpen(true)}
                            onExecuteAiTasks={handleExecuteAiTasks}
                            tasksView={tasksView}
                            tasksBusy={tasksBusy}
                            tasks={sortedTasks}
                            onViewChange={setTasksView}
                            onPreset={setPresetFilters}
                            taskFilters={taskFilters}
                            setTaskFilters={setTaskFilters}
                            activeProjectId={activeProjectId}
                            visibleWorkspaces={visibleWorkspaces}
                            companyUsers={companyUsers}
                            getUserLabel={getUserLabel}
                            taskStatusLabels={taskStatusLabels}
                            taskTags={taskTags}
                            getWorkspaceById={getWorkspaceById}
                            onOpenTask={openTaskDetail}
                            kanbanColumns={kanbanColumns}
                            kanbanGroup={kanbanGroup}
                            getInitials={getInitials}
                            onDragStart={handleKanbanDragStart}
                            onDrop={handleKanbanDrop}
                            marketplaces={marketplaces}
                            deals={crmDeals}
                            onRefresh={loadTasks}
                            companyId={companyId}
                          />
                        )}

                      {activeSection === "help" && <HelpSection />}

                      {activeSection === "crm" && isModuleVisible("crm") && (
                        <CrmSection
                          companyMode={companyMode}
                          workspaces={visibleWorkspaces}
                          activeWorkspaceId={activeProjectId}
                          onWorkspaceChange={setActiveProjectId}
                          onCreateTaskForDeal={handleCreateTaskForDeal}
                          onOpenTasksForDeal={handleOpenTasksForDeal}
                        />
                      )}


                      {activeSection === "chat" && isModuleVisible("chat") && (
                        <ChatSection
                          workspaceId={activeProjectId}
                          workspaces={visibleWorkspaces}
                          onWorkspaceChange={setActiveProjectId}
                          sessions={aiChat.sessions}
                          sessionsLoading={aiChat.sessionsLoading}
                          activeSessionId={aiChat.activeSessionId}
                          messages={aiChat.messages}
                          inputValue={aiChat.inputValue}
                          busy={aiChat.busy}
                          progressStep={aiChat.progressStep}
                          pendingConfirmation={aiChat.pendingConfirmation}
                          onCreateSession={aiChat.onCreateSession}
                          onSelectSession={aiChat.onSelectSession}
                          onDeleteSession={aiChat.onDeleteSession}
                          onRenameSession={aiChat.onRenameSession}
                          onClearMessages={aiChat.onClearMessages}
                          onInputChange={aiChat.onInputChange}
                          onInputKeyDown={aiChat.onInputKeyDown}
                          onSend={aiChat.onSend}
                          onMessageFeedback={aiChat.onMessageFeedback}
                        />
                      )}

                      {activeSection === "marketplace" &&
                        isModuleVisible("marketplace") && (
                          <MarketplaceSection
                            activeMarketplace={activeMarketplace}
                            ozonProps={ozonSectionProps}
                            wbProps={wbSectionProps}
                            moduleFlags={moduleFlags}
                          />
                        )}

                      {showDisabledSectionMessage && (
                        <div className="panel empty">
                          Этот модуль отключен администратором.
                        </div>
                      )}
                      {isBalanceRestricted && (
                        <div className="panel empty">
                          Баланс нулевой. Доступны только настройки и биллинг.
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              <AppFooter
                onOpenCookieSettings={openCookieSettings}
                setActiveSection={setActiveSection}
                setSettingsTab={setSettingsTab}
              />

              <EditAccountModal
                editAccount={editAccount}
                editForm={editForm}
                setEditForm={setEditForm}
                editBusy={editBusy}
                onClose={closeEditAccount}
                onSubmit={handleEditSubmit}
              />

              <TaskCreateModal
                open={taskModalOpen}
                onClose={() => setTaskModalOpen(false)}
                onSubmit={handleCreateTask}
                taskForm={taskForm}
                setTaskForm={setTaskForm}
                user={user}
                visibleWorkspaces={visibleWorkspaces}
                deals={crmDeals}
                taskTagsInput={taskTagsInput}
                setTaskTagsInput={setTaskTagsInput}
                handleTagsBlur={handleTagsBlur}
                handleTagsKeyDown={handleTagsKeyDown}
                companyUsers={companyUsers}
                getUserLabel={getUserLabel}
                toggleTaskList={toggleTaskList}
                marketplaces={marketplaces}
                tasksBusy={tasksBusy}
                request={request}
                showStatus={showStatus}
                panelRef={taskCreatePanelRef}
                panelStyle={taskCreateSize ? { width: `${taskCreateSize.width}px`, height: `${taskCreateSize.height}px` } : undefined}
                onResizeStart={startTaskCreateResize}
              />

              <TaskDetailModal
                open={!!selectedTask}
                onClose={closeTaskDetail}
                taskDetailPanelRef={taskDetailPanelRef}
                taskDetailSize={taskDetailSize}
                taskDetailBusy={taskDetailBusy}
                taskDetail={taskDetail}
                user={user}
                openTaskEdit={openTaskEdit}
                taskDescriptionExpanded={taskDescriptionExpanded}
                setTaskDescriptionExpanded={setTaskDescriptionExpanded}
                companyUsers={companyUsers}
                getUserLabel={getUserLabel}
                taskStatusLabels={taskStatusLabels}
                marketplaces={marketplaces}
                getWorkspaceById={getWorkspaceById}
                taskHistory={taskHistory}
                taskHistoryExpanded={taskHistoryExpanded}
                setTaskHistoryExpanded={setTaskHistoryExpanded}
                formatHistoryEntry={formatHistoryEntry}
                formatDateTime={formatDateTime}
                taskComments={taskComments}
                taskCommentBody={taskCommentBody}
                taskCommentFiles={taskCommentFiles}
                setTaskCommentBody={setTaskCommentBody}
                setTaskCommentFiles={setTaskCommentFiles}
                handleAddTaskComment={handleAddTaskComment}
                handleStatusChange={handleStatusChange}
                handleExecuteAiTask={handleExecuteAiTask}
                handleDeleteTask={handleDeleteTask}
                startTaskResize={startTaskResize}
                request={request}
                API_BASE_URL={API_BASE_URL}
                showStatus={showStatus}
              />

              <TaskEditModal
                open={taskEditOpen}
                onClose={closeTaskEdit}
                panelRef={taskEditPanelRef}
                panelStyle={
                  taskEditSize
                    ? {
                        width: `${taskEditSize.width}px`,
                        height: `${taskEditSize.height}px`,
                      }
                    : undefined
                }
                onSubmit={handleUpdateTask}
                taskDetail={taskDetail}
                user={user}
                taskEditForm={taskEditForm}
                setTaskEditForm={setTaskEditForm}
                visibleWorkspaces={visibleWorkspaces}
                deals={crmDeals}
                taskEditTagsInput={taskEditTagsInput}
                setTaskEditTagsInput={setTaskEditTagsInput}
                handleEditTagsBlur={handleEditTagsBlur}
                handleEditTagsKeyDown={handleEditTagsKeyDown}
                companyUsers={companyUsers}
                getUserLabel={getUserLabel}
                toggleEditList={toggleEditList}
                marketplaces={marketplaces}
                taskEditBusy={taskEditBusy}
                onResizeStart={startTaskEditResize}
              />

              <ProjectModal
                open={projectModalOpen}
                onClose={closeProjectModal}
                title={projectEditId ? "Изменить проект" : "Создать проект"}
                onSubmit={saveProject}
                projectForm={projectForm}
                setProjectForm={setProjectForm}
                availableProjectCabinets={availableProjectCabinets}
                toggleProjectCabinet={toggleProjectCabinet}
                getUserLabel={getUserLabel}
                setActiveSection={setActiveSection}
                setSettingsTab={setSettingsTab}
                busy={busy}
                projectEditId={projectEditId}
                onDelete={handleDeleteProject}
                currentUserId={user?.id}
              />

              <Lightbox
                lightbox={lightbox}
                onClose={closeLightbox}
                onNavigate={goLightbox}
                onCopy={handleCopy}
              />
            </main>
            </div>
          }
        />
      </Routes>
      <CookieConsentBanner />
    </>
  );
}

export default App;
